/**
 * Subscriptions Page
 *
 * Displays the current user's exclusive-content subscriptions in a 3-column
 * avatar grid. Mirrors Flutter's SubscriptionsScreen
 * (lib/presentation/screens/subscriptions/subscriptions_screen.dart).
 *
 * Data source: the existing `exclusiveContent` Redux slice. The live Firestore
 * listener in `ExclusiveContentListener` keeps the slice in sync, so new and
 * cancelled subscriptions update the page automatically without a refresh —
 * this page performs no fetching of its own.
 *
 * Architecture: Page → useSubscriptions Hook → Redux slice (listener-backed)
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useBasePage, useGridColumnsCount } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { UserAvatarGridItem } from '@/presentation/components/common/user/UserAvatarGridItem';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import { useSubscriptions } from './hooks/useSubscriptions';

export const SubscriptionsPage = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const gridColumnsCount = useGridColumnsCount();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const { subscriptions, isLoading } = useSubscriptions();

  useBasePage({
    screenKey,
    cleanReactQueryBehaviour: 'onPop',
  });

  const handleBack = () => {
    Navigator.goBack();
  };

  const handleSubscriptionClick = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderSubscriptionItem = useCallback(
    (subscription: ExclusiveContentSubscriptionEntity) => (
      <UserAvatarGridItem
        userId={subscription.userId}
        name={subscription.name}
        onClick={handleSubscriptionClick}
        qa={{ id: `subscription-${subscription.userId}` }}
      />
    ),
    [handleSubscriptionClick],
  );

  // Coarse lifecycle token for the page root, derived from existing state only.
  const pageStatus = isLoading && subscriptions.length === 0
    ? 'loading'
    : subscriptions.length === 0
      ? 'empty'
      : 'ready';

  return (
    <FlexibleHeaderList
      qa={{ id: 'subscriptions', status: pageStatus }}
      listQa={{ id: 'subscriptions-list' }}
      items={subscriptions}
      renderItem={renderSubscriptionItem}
      getItemKey={(subscription) => subscription.userId}
      gridProps={{
        columnsCount: gridColumnsCount,
        crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
        itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
      }}
      emptyListComponent={
        <ContentGridItemState
          isLoading={isLoading}
          emptyText={l10n.subscriptions.empty}
        />
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.accountInfo.subscriptions,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              iconPath="/assets/images/subscriptions.png"
              title={l10n.accountInfo.subscriptions}
            />
          ),
        },
      }}
    />
  );
};
